import { ChangeEvent, useEffect, useState } from "react";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwButton from "../../../shared/OwButton/OwButton";
import OwToggle from "../../../shared/OwToggle/OwToggle";
import { MAX_CUSTOMER_DELIVERY_DAY_OPTIONS } from "../../../../models/DeliveryDeadlineConstants";
import { LoadingResults } from "../../../shared/LoadingResults/LoadingResults";
import { useAppContextNonNull } from "../../../../contexts/AppContext";
import NotificationLevelEnum from "../../../../models/NotificationLevelEnum";
import ISupplier_Delivery_Schedule from "../../../../models/ISupplier_Delivery_Schedule";
import ISupplier_Delivery_Schedule_Day from "../../../../models/ISupplier_Delivery_Schedule_Day";
import DeliveryDeadlinesService from "../../../../services/DeliveryDeadlinesService";
import HelperService from "../../../../services/HelperService";
import DeliveryScheduleDayForm from "./DeliveryScheduleDayForm";
import { DAY_ABBR_KEYS, format12h, sortDays } from "./deliveryDeadlineUtils";
import "./DeliveryScheduleEditor.scss";

type Props = {
    scheduleGuid: string;
    /** Called after any change that the templates list should reflect (name/active/day counts). */
    onChanged: () => void;
    onClose: () => void;
};

type DayEditTarget = { mode: "new" } | { mode: "edit"; day: ISupplier_Delivery_Schedule_Day } | null;

/**
 * Manages a single schedule template: rename, activate/deactivate, and CRUD on its
 * delivery days. Self-contained REST loads keep it in sync after each mutation.
 */
export const DeliveryScheduleEditor = (props: Props) => {
    const { userModel, supplierModel, uiContext } = useAppContextNonNull();

    const [schedule, setSchedule] = useState<ISupplier_Delivery_Schedule | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [name, setName] = useState("");
    const [isSavingMeta, setIsSavingMeta] = useState(false);
    // Bumped when a settings save fails: OwToggle keeps its own flipped state, so remounting the toggles
    // (via key) is what puts them back on the server value.
    const [toggleVersion, setToggleVersion] = useState(0);
    const [dayTarget, setDayTarget] = useState<DayEditTarget>(null);

    /**
     * Loads the template. `keepCurrentOnFailure` is used by the save-failure recovery path: a reload
     * that fails then leaves the last loaded template (and the editor) in place instead of blanking it.
     */
    const loadSchedule = async (keepCurrentOnFailure = false) => {
        setIsLoading(true);
        if (!keepCurrentOnFailure) {
            setSchedule(null);
            setName("");
        }
        try {
            const data = await DeliveryDeadlinesService.getDeliverySchedule(props.scheduleGuid, userModel, supplierModel);
            setSchedule(data.deliverySchedule);
            setName(data.deliverySchedule.name);
        } catch (error) {
            uiContext.showNotification({ message: (error as Error).message, level: NotificationLevelEnum.error });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadSchedule();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.scheduleGuid]);

    const saveName = async () => {
        if (!schedule || !name.trim() || name.trim() === schedule.name) return;
        setIsSavingMeta(true);
        try {
            await DeliveryDeadlinesService.updateDeliverySchedule(schedule.guid, { name: name.trim() }, userModel, supplierModel);
            uiContext.showNotification({ message: "delivery_schedule_saved", level: NotificationLevelEnum.success });
            await loadSchedule();
            props.onChanged();
        } catch (error) {
            uiContext.showNotification({ message: (error as Error).message, level: NotificationLevelEnum.error });
        } finally {
            setIsSavingMeta(false);
        }
    };

    /**
     * Saves one or more header settings (active, customer choice) and refreshes the template. On failure
     * the template is reloaded and the toggles remounted so the editor shows the server state again.
     */
    const saveSettings = async (updates: Partial<ISupplier_Delivery_Schedule>) => {
        if (!schedule) return;
        setIsSavingMeta(true);
        try {
            await DeliveryDeadlinesService.updateDeliverySchedule(schedule.guid, updates, userModel, supplierModel);
            uiContext.showNotification({ message: "delivery_schedule_saved", level: NotificationLevelEnum.success });
            await loadSchedule();
            props.onChanged();
        } catch (error) {
            uiContext.showNotification({ message: (error as Error).message, level: NotificationLevelEnum.error });
            await loadSchedule(true);
            setToggleVersion((version) => version + 1);
        } finally {
            setIsSavingMeta(false);
        }
    };

    const toggleActive = (e: any) => saveSettings({ active: e.target.value as boolean });

    const toggleCustomerChoice = (e: any) => saveSettings({ customer_can_choose_delivery_day: e.target.value as boolean });

    const changeOptionCount = (e: ChangeEvent<HTMLSelectElement>) =>
        saveSettings({ customer_delivery_day_options: Number(e.target.value) });

    const onDaySaved = async () => {
        setDayTarget(null);
        await loadSchedule();
        props.onChanged();
    };

    const deleteDay = async (day: ISupplier_Delivery_Schedule_Day) => {
        try {
            await DeliveryDeadlinesService.deleteDeliveryScheduleDay(schedule!.guid, day.guid, userModel, supplierModel);
            uiContext.showNotification({ message: "delivery_day_deleted", level: NotificationLevelEnum.success });
            await loadSchedule();
            props.onChanged();
        } catch (error) {
            uiContext.showNotification({ message: (error as Error).message, level: NotificationLevelEnum.error });
        }
    };

    if (isLoading) {
        return <LoadingResults />;
    }

    if (!schedule) {
        return null;
    }

    const days = sortDays(schedule.Days);

    return (
        <div className="DeliveryScheduleEditor rounded p-3 mt-3">
            <div className="d-flex justify-content-between align-items-center">
                <OwTitle title="edit_template" tag="h5" leftIconName="fad fa-calendar-edit" />
                <OwButton fn={props.onClose} centerIconClass="fas fa-times" className="btn-secondary btn-sm" ariaLabel="close" />
            </div>

            <div className="row g-3 align-items-end mt-1">
                <div className="col-md-6">
                    <OwLabel title="name" noBottomMargin={true} />
                    <input
                        type="text"
                        aria-label="template name"
                        className="form-control form-control-sm"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                </div>
                <div className="col-md-3">
                    <OwButton
                        title="save"
                        fn={saveName}
                        className="btn-primary btn-sm"
                        disabled={isSavingMeta || !name.trim() || name.trim() === schedule.name}
                    />
                </div>
                <div className="col-md-3">
                    <OwToggle
                        key={`active-${toggleVersion}`}
                        fn={toggleActive}
                        name="active"
                        label="active"
                        active={!!schedule.active}
                        labelOnRight={true}
                        disabled={isSavingMeta}
                    />
                </div>
            </div>

            <div className="row g-3 align-items-start mt-1">
                <div className="col-md-8">
                    <OwToggle
                        key={`customer-choice-${toggleVersion}`}
                        fn={toggleCustomerChoice}
                        name="customer_can_choose_delivery_day"
                        label="customer_can_choose_delivery_day"
                        active={!!schedule.customer_can_choose_delivery_day}
                        labelOnRight={true}
                        disabled={isSavingMeta}
                    />
                    <OwLabel title="help_text_customer_can_choose_delivery_day" className="text-muted small" noBottomMargin={true} />
                </div>
                {schedule.customer_can_choose_delivery_day ? (
                    <div className="col-md-4">
                        <OwLabel title="customer_delivery_day_options" noBottomMargin={true} />
                        <select
                            aria-label="delivery day options"
                            className="form-select form-select-sm"
                            value={schedule.customer_delivery_day_options || 1}
                            disabled={isSavingMeta}
                            onChange={changeOptionCount}
                        >
                            {Array.from({ length: MAX_CUSTOMER_DELIVERY_DAY_OPTIONS }, (_, i) => i + 1).map((n) => (
                                <option key={n} value={n}>
                                    {n}
                                </option>
                            ))}
                        </select>
                    </div>
                ) : null}
            </div>

            <hr />

            <div className="d-flex justify-content-between align-items-center">
                <OwLabel title="delivery_days" className="fw-bold" />
                <OwButton
                    title="add_day"
                    leftIconClass="fas fa-plus"
                    fn={() => setDayTarget({ mode: "new" })}
                    className="btn-outline-primary btn-sm"
                    disabled={!!dayTarget}
                />
            </div>

            {days.length === 0 && !dayTarget ? (
                <OwLabel title="no_delivery_days_yet" className="text-muted mt-2" />
            ) : (
                <div className="table-responsive mt-2">
                    <table className="table table-sm align-middle">
                        <thead>
                            <tr>
                                <th>
                                    <OwLabel title="delivery_day" noBottomMargin={true} />
                                </th>
                                <th>
                                    <OwLabel title="soft_reminder" noBottomMargin={true} />
                                </th>
                                <th>
                                    <OwLabel title="hard_deadline" noBottomMargin={true} />
                                </th>
                                <th>
                                    <OwLabel title="minimum_order_total" noBottomMargin={true} />
                                </th>
                                <th className="text-end">
                                    <OwLabel title="actions" noBottomMargin={true} />
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {days.map((day) => (
                                <tr key={day.id}>
                                    <td>
                                        <OwLabel title={DAY_ABBR_KEYS[day.delivery_day_of_week]} noBottomMargin={true} />
                                    </td>
                                    <td>
                                        {day.soft_reminder_day_of_week != null && day.soft_reminder_time ? (
                                            <>
                                                <OwLabel title={DAY_ABBR_KEYS[day.soft_reminder_day_of_week]} noBottomMargin={true} />
                                                <span className="ms-1">{format12h(day.soft_reminder_time)}</span>
                                            </>
                                        ) : (
                                            <OwLabel title="none" className="text-muted" noBottomMargin={true} />
                                        )}
                                    </td>
                                    <td>
                                        <OwLabel
                                            title={DAY_ABBR_KEYS[day.hard_deadline_day_of_week]}
                                            noBottomMargin={true}
                                            skipTranslate={false}
                                        />
                                        <span className="ms-1">{format12h(day.hard_deadline_time)}</span>
                                    </td>
                                    <td>
                                        {day.minimum_order_total != null ? (
                                            HelperService.formatCurrency({ value: Number(day.minimum_order_total) })
                                        ) : (
                                            <OwLabel title="none" className="text-muted" noBottomMargin={true} />
                                        )}
                                    </td>
                                    <td className="text-end">
                                        <OwButton
                                            centerIconClass="fas fa-pen"
                                            fn={() => setDayTarget({ mode: "edit", day })}
                                            className="btn-outline-secondary btn-sm me-2"
                                            ariaLabel="edit day"
                                            disabled={!!dayTarget}
                                        />
                                        <OwButton
                                            centerIconClass="fas fa-trash"
                                            fn={() => deleteDay(day)}
                                            className="btn-outline-danger btn-sm"
                                            ariaLabel="delete day"
                                            disabled={!!dayTarget}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {dayTarget && (
                <DeliveryScheduleDayForm
                    scheduleGuid={schedule.guid}
                    existingDay={dayTarget.mode === "edit" ? dayTarget.day : undefined}
                    onSaved={onDaySaved}
                    onCancel={() => setDayTarget(null)}
                />
            )}
        </div>
    );
};

export default DeliveryScheduleEditor;
